import { useEffect, useMemo, useRef, useState } from 'react'
import { preparePhoto } from './prepare-photo'
import type { FotoPreparada } from './prepare-photo'

/**
 * Escolher a foto de um produto — o que ainda vai nascer e o que já existe.
 *
 * ## Por que ela não sobe na hora
 *
 * O caminho no Storage é o id do produto (`households/{hid}/products/{id}`), e
 * no cadastro esse id ainda não existe. Subir para um caminho temporário e mover
 * depois seria duas escritas e um arquivo órfão sempre que alguém desistisse do
 * cadastro. Segurar o blob em memória e subir depois do `create` é mais simples
 * e não deixa lixo.
 *
 * ## Mas ela é PREPARADA na hora
 *
 * Encolher no instante da escolha, e não no `salvar`, dá duas coisas: a pessoa
 * vê que funcionou (e quanto economizou) enquanto ainda pode trocar de foto, e o
 * salvamento fica sendo só um upload — sem um segundo de processamento entre o
 * toque e o produto aparecer na lista.
 *
 * ## Ela também EDITA, e por isso conhece a foto já salva
 *
 * Quando a folha abre sobre um produto existente, `atual` traz a URL guardada.
 * A prévia mostra a nova quando há uma, e a salva quando não há — e "Tirar a
 * foto" aparece nos dois casos. Sem `atual`, editar um produto com foto abriria
 * a folha parecendo que ele não tem nenhuma, e o único jeito de trocar seria
 * escolher outra às cegas.
 *
 * **Ela não apaga nada por conta própria.** Tirar aqui é uma intenção; quem a
 * executa é o `salvar` da folha, junto com o resto — pelo mesmo motivo de a
 * foto nova não subir na hora. Um campo que grava sozinho no meio de um
 * formulário que grava no fim é duas regras na mesma tela.
 *
 * ## O `objectURL` é revogado
 *
 * Uma URL de objeto segura o blob inteiro na memória até alguém revogar. Trocar
 * de foto cinco vezes num celular seguraria cinco fotos — e ninguém reporta
 * isso como bug, só sente o app ficando pesado.
 */
export function EscolherFoto({
  foto,
  onEscolher,
  atual = null,
}: {
  foto: FotoPreparada | null
  onEscolher: (foto: FotoPreparada | null) => void
  /** A foto que o produto já tem, quando a folha está editando um. */
  atual?: string | null
}) {
  const input = useRef<HTMLInputElement>(null)
  const [preparando, setPreparando] = useState(false)

  // A URL sai de um `useMemo`, e não de estado gravado num efeito: com o estado,
  // trocar de foto mostraria a ANTERIOR por um render — e o lint recusa
  // `setState` síncrono em efeito justamente por isso. O efeito abaixo só
  // revoga.
  const previa = useMemo(() => (foto === null ? null : URL.createObjectURL(foto.blob)), [foto])

  useEffect(() => {
    if (previa === null) return
    return () => {
      // Sem isto o blob fica na memória até a aba fechar: trocar de foto cinco
      // vezes num celular seguraria cinco fotos, e ninguém reporta isso como
      // bug — só sente o app ficando pesado.
      URL.revokeObjectURL(previa)
    }
  }, [previa])

  return (
    <div className="text-sm sm:col-span-2">
      <span className="block text-xs text-[var(--text-muted)]">Foto</span>

      {previa === null ? (
        atual !== null && (
          <img src={atual} alt="Foto atual do produto" className="mb-2 max-h-40 rounded-lg" />
        )
      ) : (
        <img src={previa} alt="Prévia da foto do produto" className="mb-2 max-h-40 rounded-lg" />
      )}

      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-label="Foto do produto"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file === undefined) return
          setPreparando(true)
          void preparePhoto(file)
            .then(onEscolher)
            .catch((erro: unknown) => {
              // `preparePhoto` já devolve o original em vez de lançar; se
              // chegou aqui é outra coisa, e a foto simplesmente não entra.
              console.error('EscolherFoto: não deu para preparar', erro)
            })
            .finally(() => {
              setPreparando(false)
            })
        }}
      />

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={preparando}
          className="rounded-lg border border-[var(--border)] alvo px-3 py-1 text-sm disabled:opacity-50"
          onClick={() => {
            input.current?.click()
          }}
        >
          {preparando
            ? 'Preparando…'
            : foto === null && atual === null
              ? 'Tirar ou escolher uma foto'
              : 'Trocar'}
        </button>

        {(foto !== null || atual !== null) && (
          <button
            type="button"
            className="alvo justify-start text-xs underline"
            onClick={() => {
              onEscolher(null)
            }}
          >
            Tirar a foto
          </button>
        )}

        {foto?.processada === true && (
          <span className="text-xs text-[var(--text-muted)]">
            {`${kb(foto.bytesOriginais)} → ${kb(foto.bytesFinais)} em ${foto.formato.replace('image/', '').toUpperCase()}`}
          </span>
        )}
      </div>
    </div>
  )
}

function kb(bytes: number): string {
  return `${String(Math.round(bytes / 1024))} KB`
}
